import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { userQueryConfig, UserSummary } from '@webapp/platform/iam'

/**
 * Fetches the stored specific-user contact by id. `UserSelect` only lists
 * active users, so a stored user who was later deactivated would silently
 * drop out of its options — fetching by id keeps the picker's displayed
 * value correct even for a deactivated selection.
 *
 * `enabled: userId != null` means the very first specific-user save starts
 * this query from disabled with nothing cached — callers should not fold
 * `isLoading` from this hook into a mount/unmount decision; let the consuming
 * input show its own loading state instead. The query is keyed by id with no
 * previous-data carry-over, so a re-keyed lookup reports `contactUser: null`
 * and `isLoading: true` until the new user resolves, and a failed lookup
 * reports `null` with `isError`.
 */
export const useQueryPOBillToContactUser = (userId: Nullable<number>) => {
  const { data, isLoading, isError } = useQuery({
    ...userQueryConfig.getBasicDetails(userId),
    enabled: userId != null,
  })

  const contactUser = useMemo(
    () => (data?.data ? new UserSummary(data.data) : null),
    [data]
  )

  return {
    contactUser,
    isLoading,
    isError,
  }
}
